/**
 * Phase 8 T-17 §23.B — Global toast provider + context.
 *
 * Wrap the app root with `<ToastProvider>` to enable `useGlobalToast()` in
 * any descendant. The provider owns the toast queue and renders
 * `<ToastContainer>` via a React portal into `document.body`.
 *
 * Auto-dismiss timers per §23.B:
 *   - success / info / warning → 4 200 ms
 *   - error                    → 7 000 ms
 *
 * The existing per-component `useToast` hook in `@shared/hooks/useToast` is
 * intentionally preserved for backward compat. New code should use
 * `useGlobalToast` which routes into this provider.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { ToastItem, ToastVariant } from './Toast';
import { ToastContainer } from './ToastContainer';
import { registerListExportToast } from '@shared/lib/listExportStub';

export interface GlobalToastOptions {
  title: string;
  body?: string;
  /** @default 'success' */
  variant?: ToastVariant;
  /**
   * Backward-compat alias for `variant`. `kind: 'error'` → `variant: 'error'`.
   * @deprecated Use `variant` instead.
   */
  kind?: 'success' | 'error';
  /** Override auto-dismiss duration (ms). Default is variant-based. */
  duration?: number;
}

interface ToastContextValue {
  showToast: (opts: GlobalToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4200,
  info: 4200,
  warning: 4200,
  error: 7000,
};

const MAX_QUEUE = 3;

let _counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const showToast = useCallback(
    (opts: GlobalToastOptions) => {
      // Resolve variant (support legacy `kind` alias).
      const variant: ToastVariant =
        opts.variant ?? (opts.kind === 'error' ? 'error' : 'success');

      const id = ++_counter;
      const item: ToastItem = {
        id,
        title: opts.title,
        body: opts.body,
        variant,
      };

      setQueue((prev) => {
        // Newest first; evict the oldest if over the cap.
        const next = [item, ...prev];
        if (next.length > MAX_QUEUE) {
          const evicted = next.pop()!;
          const t = timers.current.get(evicted.id);
          if (t) {
            clearTimeout(t);
            timers.current.delete(evicted.id);
          }
        }
        return next;
      });

      const duration = opts.duration ?? DEFAULT_DURATION[variant];
      const timer = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    registerListExportToast(showToast);
    return () => registerListExportToast(null);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={queue} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * Access the global toast queue from any component inside `<ToastProvider>`.
 *
 * @example
 * const { showToast } = useGlobalToast();
 * showToast({ title: 'Saved', variant: 'success' });
 * showToast({ title: 'Failed', variant: 'error' });
 */
export function useGlobalToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Provide a no-op fallback so callers don't crash before the provider
    // is mounted (e.g. during unit tests or Storybook stories).
    return {
      showToast: (opts) => {
        // eslint-disable-next-line no-console
        console.warn('[useGlobalToast] called outside <ToastProvider>', opts);
      },
    };
  }
  return ctx;
}
